<template>
  <layout-public>
    <page-meta />
    <page-hero
      :crumbs="[
        { label: t('Home'), href: route('index') },
        { label: t('Services'), href: route('index') + '#services' },
        { label: t('Digital marketing') }
      ]"
      :title="t('Digital Marketing')"
      :lede="t('Rank for the phrases your buyers actually type into Google, and reach the rest with paid ads starting from the very first day following launch.')"
    />

    <service-block
      variant="plain"
      :kicker="t('Being found is the whole point.')"
      :title="t('SEO builds over months.\nAds start today.')"
      :lede="t('Your buyers are already searching Google. SEO claims the phrases that matter, so page one keeps paying you back in steady, free organic enquiries.')"
      :aside-title="t('Every plan includes')"
      :aside-items="dmIncludes"
    >
      <div class="action-row">
        <app-button href="#seo-plans" icon="arrow-down">
          {{ t('See the plans') }}
        </app-button>
      </div>
    </service-block>

    <plan-table
      block-id="seo-plans"
      :kicker="t('Paying less does not mean getting less.')"
      :title="t('SEO plans')"
      :lede="t('You buy the position itself rather than rented clicks. Name the phrases your buyers type and we handle the research, the content and the links.')"
      :price-label="t('Price')"
      :price-hint="t('What you pay each month for the plan.')"
      :columns="seoColumns"
      :rows="seoRows"
      :remarks="seoRemarks"
    />

    <why-grid
      :kicker="t('Why use SEO')"
      :title="t('Own page one for the phrases that pay')"
      :lede="t('Pause your advertising spending and the enquiries dry up on the very same day, whilst those pages and links you built with SEO keep working.')"
      :items="seoWhy"
    />

    <plan-table
      block-id="ads-plans"
      :kicker="t('Paying less does not mean getting less.')"
      :title="t('Ads management plans')"
      :lede="t('Enquiries arrive this week, rather than next quarter. We plan, test, and report on the campaigns for you, and adjust as the numbers roll in.')"
      :price-label="t('Price')"
      :price-hint="t('What you pay each month for the plan.')"
      :columns="adsColumns"
      :rows="adsRows"
      :remarks="adsRemarks"
    />

    <why-grid
      :kicker="t('Why use sponsored ads')"
      :title="t('Buyers this week, on your budget')"
      :lede="t('Advertising places you in front of people who match your buyer profile. You control the budget, and the campaign can be paused at any moment.')"
      :items="adsWhy"
    />

    <faq-list
      :kicker="t('Before you decide')"
      :title="t('Frequently asked questions')"
      :lede="t('What SEO and paid advertising actually cover, how our fee sits separately from the ad budget, and exactly what you should expect before you commit.')"
      :items="faqs"
      accordion-id="digital-marketing-faq"
    />
  </layout-public>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The .dm-actions row is gone: the button owns its own hover and the gap comes
   from the global .action-row. The panels this page renders - the service-block
   aside and the why cards - own their own motion in their components. */
</style>


<script>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  faArrowDown,
  faBolt,
  faBullhorn,
  faBullseye,
  faCalendarCheck,
  faChartLine,
  faCircleCheck,
  faClipboardCheck,
  faLocationDot,
  faMagnifyingGlass,
  faMousePointer,
  faWallet
} from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import PageMeta from '@/Components/PageMeta.vue';
import AppButton from '@/Components/AppButton.vue';
import FaqList from '@/Components/FaqList.vue';
import LayoutPublic from '@/Layouts/Public.vue';
import PageHero from '@/Components/PageHero.vue';
import PlanTable from '@/Components/PlanTable.vue';
import ServiceBlock from '@/Components/ServiceBlock.vue';
import WhyGrid from '@/Components/WhyGrid.vue';

FontAwesomeLibrary.add(
  faArrowDown,
  faBolt,
  faBullhorn,
  faBullseye,
  faCalendarCheck,
  faChartLine,
  faCircleCheck,
  faClipboardCheck,
  faLocationDot,
  faMagnifyingGlass,
  faMousePointer,
  faWallet
);

export default {
  components: {
    AppButton,
    FaqList,
    PageMeta,
    LayoutPublic,
    PageHero,
    PlanTable,
    ServiceBlock,
    WhyGrid
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'What you pay each month for the plan.': 'What you pay each month for the plan.',
          'Start with search': 'Start with search',
          'Grow your rankings': 'Grow your rankings',
          'Scale your reach': 'Scale your reach',
          'Start ranking for the phrases that matter most.': 'Start ranking for the phrases that matter most.',
          'More phrases, more pages, more ways to be found.': 'More phrases, more pages, more ways to be found.',
          'Maximum reach, with content and closer checks.': 'Maximum reach, with content and closer checks.',
          'Test your first campaign': 'Test your first campaign',
          'Grow your enquiries': 'Grow your enquiries',
          'Scale your spend': 'Scale your spend',
          'One campaign to prove what converts.': 'One campaign to prove what converts.',
          'More campaigns, plus a landing page for the click.': 'More campaigns, plus a landing page for the click.',
          'Spend more, run more campaigns, keep the landing page.': 'Spend more, run more campaigns, keep the landing page.',
          'No setup fee required for any of the plans.': 'No setup fee required for any of the plans.',
          'Rankings are measured on Google page 1 within 12 months of work starting.': 'Rankings are measured on Google page 1 within 12 months of work starting.',
          'Ads spend is prepaid, and the range above is your media budget, not our fee.': 'Ads spend is prepaid, and the range above is your media budget, not our fee.',
          'Pro plan will add an additional 5% on the amount of ads spend above RM 10,000.': 'Pro plan will add an additional 5% on the amount of ads spend above RM 10,000.',
          '10 keywords on Google\'s first page': '10 keywords on Google\'s first page',
          '15 keywords on Google\'s first page': '15 keywords on Google\'s first page',
          '5 keywords on Google\'s first page': '5 keywords on Google\'s first page',
          'A landing page built to close the click.': 'A landing page built to close the click.',
          'Ads management plans': '**Ads management** plans',
          'Ads spend': 'Ads spend',
          'Built to bring enquiries, not views.': 'Built to bring enquiries, not views.',
          'Campaign tests': 'Campaign tests',
          'Campaigns': 'Campaigns',
          'Competitor analysis': 'Competitor analysis',
          'Content posts': 'Content posts',
          'Content strategy': 'Content strategy',
          'SEO builds over months.\nAds start today.': 'SEO builds over months.\nAds start **today**.',
          'Your buyers are already searching Google. SEO claims the phrases that matter, so page one keeps paying you back in steady, free organic enquiries.': 'Your buyers are already searching Google. SEO claims the phrases that matter, so page one keeps paying you back in steady, free organic enquiries.',
          'Every 3 months': 'Every 3 months',
          'Free landing page': 'Free landing page',
          'What SEO and paid advertising actually cover, how our fee sits separately from the ad budget, and exactly what you should expect before you commit.': 'What SEO and paid advertising actually cover, how our fee sits separately from the ad budget, and exactly what you should expect before you commit.',
          'Google Business Profile': 'Google Business Profile',
          'Google, Facebook or TikTok?': 'Google, Facebook or TikTok?',
          'I already have a site. Can you only do SEO?': 'I already have a site. Can you only do SEO?',
          'I need traffic now. Is that SEO or ads?': 'I need traffic now. Is that SEO or ads?',
          'Is SEO included in web design fees?': 'Is SEO included in web design fees?',
          'Is the ads budget on top of your fee?': 'Is the ads budget on top of your fee?',
          'Keyword research': 'Keyword research',
          'Keywords': 'Keywords',
          'How many search phrases we work on for your business.': 'How many search phrases we work on for your business.',
          'A check of your website so we know what to fix first.': 'A check of your website so we know what to fix first.',
          'We pick the phrases people type when they look for you.': 'We pick the phrases people type when they look for you.',
          'The title and short text Google shows in search.': 'The title and short text Google shows in search.',
          'Changes on your site, plus links from other sites.': 'Changes on your site, plus links from other sites.',
          'A report that shows how people find and use the site.': 'A report that shows how people find and use the site.',
          'Written pages we publish to help you show up in search.': 'Written pages we publish to help you show up in search.',
          'Links from other websites that point people to yours.': 'Links from other websites that point people to yours.',
          'Your listing on Google Maps and local search.': 'Your listing on Google Maps and local search.',
          'A monthly note on where your phrases sit in Google.': 'A monthly note on where your phrases sit in Google.',
          'A look at what similar businesses are doing in search.': 'A look at what similar businesses are doing in search.',
          'How many phrases we aim to place on Google\'s first page.': 'How many phrases we aim to place on Google\'s first page.',
          'The prepaid media budget for the ads, not our fee.': 'The prepaid media budget for the ads, not our fee.',
          'How many ad sets we can run at the same time.': 'How many ad sets we can run at the same time.',
          'A plan for what the ads say and who they should reach.': 'A plan for what the ads say and who they should reach.',
          'We keep adjusting the ads and try different versions.': 'We keep adjusting the ads and try different versions.',
          'A report that shows what the ads send to your website.': 'A report that shows what the ads send to your website.',
          'A monthly note on results, with what to try next.': 'A monthly note on results, with what to try next.',
          'The ads are aimed at sales and enquiries, not just views.': 'The ads are aimed at sales and enquiries, not just views.',
          'A one-page site built for the ads to send people to.': 'A one-page site built for the ads to send people to.',
          'Meta title and description': 'Meta title and description',
          'Monthly ranking report': 'Monthly ranking report',
          'On-page and off-page work': 'On-page and off-page work',
          'Once before we start': 'Once before we start',
          'Your buyers are searching right now.': 'Your buyers are searching right now.',
          'Performance in 12 months': 'Performance in 12 months',
          'Plus plan': 'Plus plan',
          'Pro plan': 'Pro plan',
          'RM 10,000 and above': 'RM 10,000 and above',
          'RM 2,000 to RM 4,999': 'RM 2,000 to RM 4,999',
          'RM 5,000 to RM 9,999': 'RM 5,000 to RM 9,999',
          'Sales and leads': 'Sales and leads',
          'SEO plans': '**SEO** plans',
          'Digital Marketing': 'Digital Marketing',
          'Rank for the phrases your buyers actually type into Google, and reach the rest with paid ads starting from the very first day following launch.': 'Rank for the phrases your buyers actually type into Google, and reach the rest with paid ads starting from the very first day following launch.',
          'You buy the position itself rather than rented clicks. Name the phrases your buyers type and we handle the research, the content and the links.': 'You buy the position itself rather than rented clicks. Name the phrases your buyers type and we handle the research, the content and the links.',
          'Site backlinks': 'Site backlinks',
          'Standard plan': 'Standard plan',
          'Live this week, not next quarter.': 'Live this week, not next quarter.',
          'Twice a year': 'Twice a year',
          'Enquiries arrive this week, rather than next quarter. We plan, test, and report on the campaigns for you, and adjust as the numbers roll in.': 'Enquiries arrive this week, rather than next quarter. We plan, test, and report on the campaigns for you, and adjust as the numbers roll in.',
          'Website audit': 'Website audit',
          'What is the 5 percent on the Pro plan?': 'What is the 5 percent on the Pro plan?',
          'Every plan includes': 'Every plan includes',
          'Research before spend': 'Research before spend',
          'We audit your site and your competitors first, then pick the phrases or audiences actually worth paying for.': 'We audit your site and your competitors first, then pick the phrases or audiences actually worth paying for.',
          'Installed and configured on your site, so every enquiry can be traced back to the search or ad that produced it.': 'Installed and configured on your site, so every enquiry can be traced back to the search or ad that produced it.',
          'One clear report each month: where you stand, what we spent, and what changes next.': 'One clear report each month: where you stand, what we spent, and what changes next.',
          'No setup fee': 'No setup fee',
          'You pay only for the work each month. There is nothing to pay up front to get started.': 'You pay only for the work each month. There is nothing to pay up front to get started.',
          'Being found is the whole point.': 'Being found is the whole point.',
          'Own page one for the phrases that pay': 'Own page one for the **phrases that pay**',
          'Pause your advertising spending and the enquiries dry up on the very same day, whilst those pages and links you built with SEO keep working.': 'Pause your advertising spending and the enquiries dry up on the very same day, whilst those pages and links you built with SEO keep working.',
          'It compounds. Year two starts higher.': 'It compounds. Year two starts higher.',
          'You know before you pay.': 'You know before you pay.',
          'Content and local search included.': 'Content and local search included.',
          'Buyers this week, on your budget': 'Buyers this week, on your **budget**',
          'Advertising places you in front of people who match your buyer profile. You control the budget, and the campaign can be paused at any moment.': 'Advertising places you in front of people who match your buyer profile. You control the budget, and the campaign can be paused at any moment.',
          'What does the SEO aim actually say?': 'What does the SEO aim actually say?',
          'Who manages the ads account?': 'Who manages the ads account?',
          'Who picks the keywords?': 'Who picks the keywords?',
          'Why use SEO': 'Why use SEO',
          'Why use sponsored ads': 'Why use sponsored ads',
          'We manage the ads account.': 'We manage the ads account.',
          'faq-ads-now': 'Ads. SEO rankings are measured within 12 months of work starting. SEO is billed monthly, with no setup fee and no minimum period. Ads can start as soon as the account, creative and landing page are ready.',
          'faq-aim': 'Within 12 months of work starting: Standard plan aims for at least 5 keywords on Google page one, Plus plan 10, Pro plan 15. No setup fee. Content posts, backlinks and Google Business Profile are in the plan.',
          'faq-budget': 'Yes. Ads spend is prepaid. Standard plan management is RM 680 per month. Plus plan is RM 1,680. Pro plan is RM 2,680. Prepaid media sits on top in the ranges above.',
          'faq-channel': 'Google, Facebook, TikTok, and the other platforms we support are all discussed with the ads plans. They use the same ads plan prices. The ads plan covers management, tests, analytics and a monthly report aimed at sales and enquiries.',
          'faq-included': 'No. Web design plans include analytics. SEO Standard, Plus and Pro plans are separate monthly products at RM 1,980, RM 2,980 and RM 3,980, with 10, 20 and 30 keywords we work on.',
          'faq-keywords': 'We research and propose phrases people actually type. You approve the list. Changing the whole list every month resets the 12-month ranking period for those phrases.',
          'faq-only-seo': 'Yes, if the site is technically fit. We still audit it once at the start. If the site cannot rank, we will say so before you start. SEO is billed monthly, with no setup fee.',
          'faq-own': 'We manage the ads account for you. You pay the ads spend to us and we top it up, so there is nothing for you to set up.',
          'faq-percent': 'Pro plan adds 5 percent on the amount of ads spend above RM 10,000. That is on top of the RM 2,680 management fee.',
          'why-ads-account': 'We manage the ads account for you. You pay the ads spend to us and we top it up, so there is nothing for you to set up.',
          'why-ads-leads': 'Every version gets tested, so the budget goes to whatever actually brings enquiries, not just clicks.',
          'why-ads-now': 'Once the account, creative and landing page are ready, the ads run. No 12-month wait for a first enquiry.',
          'why-ads-page': 'Plus and Pro include a page designed for the ad. Standard sends the click to a page you already have.',
          'why-seo-fit': 'We audit the site before you pay. If it cannot rank yet, we say so before you commit, not three months later.',
          'why-seo-local': 'Pages written around your keywords, your Google Business Profile and maps listing, and a monthly ranking report.',
          'why-seo-programme': 'Every page and link you earn stays earned, so year two starts where year one finished instead of resetting to zero.',
          'why-seo-search': 'Someone types what you sell every single day. Without SEO they land on a competitor instead. We put you in that list.'
        },
        'zh-MY': {
          'What you pay each month for the plan.': '您为配套每月支付的费用。',
          'Start with search': '从搜索开始',
          'Grow your rankings': '让排名成长',
          'Scale your reach': '扩大触达范围',
          'Start ranking for the phrases that matter most.': '从最重要的搜索词开始排名。',
          'More phrases, more pages, more ways to be found.': '更多搜索词、更多页面、更多被找到的机会。',
          'Maximum reach, with content and closer checks.': '最大触达，内容更多、检查更密。',
          'Test your first campaign': '先测试第一个广告',
          'Grow your enquiries': '增加询盘量',
          'Scale your spend': '扩大花费规模',
          'One campaign to prove what converts.': '一个广告活动，先证明什么能成交。',
          'More campaigns, plus a landing page for the click.': '更多广告活动，加上为点击而建的落地页。',
          'Spend more, run more campaigns, keep the landing page.': '花得更多、跑得更多，落地页留着用。',
          'No setup fee required for any of the plans.': '所有配套都不收设置费。',
          'Rankings are measured on Google page 1 within 12 months of work starting.': '排名以工作开始后 12 个月内进入谷歌第 1 页来衡量。',
          'Ads spend is prepaid, and the range above is your media budget, not our fee.': '广告花费需预付，上方区间是您的媒体预算，不是我们的服务费。',
          'Pro plan will add an additional 5% on the amount of ads spend above RM 10,000.': '专业配套会对超过 RM 10,000 的广告花费加收 5%。',
          '10 keywords on Google\'s first page': '10 个关键词进入谷歌首页',
          '15 keywords on Google\'s first page': '15 个关键词进入谷歌首页',
          '5 keywords on Google\'s first page': '5 个关键词进入谷歌首页',
          'It compounds. Year two starts higher.': '效果会累积，第二年起点更高。',
          'You know before you pay.': '付费之前，您先知道结果。',
          'Content and local search included.': '内容与本地搜索都已包含。',
          'A landing page built to close the click.': '为成交而建的落地页。',
          'Ads management plans': '**广告管理**配套',
          'Ads spend': '广告花费',
          'Built to bring enquiries, not views.': '为了带来询盘，不是刷浏览量。',
          'Campaign tests': '广告测试',
          'Campaigns': '广告活动',
          'Competitor analysis': '竞品分析',
          'Content posts': '内容文章',
          'Content strategy': '内容策略',
          'Your buyers are already searching Google. SEO claims the phrases that matter, so page one keeps paying you back in steady, free organic enquiries.': '您的客户正在 Google 寻找您卖的东西。SEO 拿下真正重要的搜索词，首页因此持续带来免费的询盘；广告今天触及同一批人，两者并行最稳。',
          'Every 3 months': '每 3 个月',
          'Free landing page': '免费落地页',
          'What SEO and paid advertising actually cover, how our fee sits separately from the ad budget, and exactly what you should expect before you commit.': 'SEO 与广告各自包含什么、我们的费用如何与广告预算分开计算，以及决定投入之前您应该先弄清楚的事，都在这里一次讲明白，绝不绕弯子。',
          'Google Business Profile': 'Google 商家档案',
          'Google, Facebook or TikTok?': '谷歌、Facebook 还是 TikTok？',
          'I already have a site. Can you only do SEO?': '我已有网站。可以只做 SEO 吗？',
          'I need traffic now. Is that SEO or ads?': '我现在就要流量。该选 SEO 还是广告？',
          'Is SEO included in web design fees?': '网页设计费用包含 SEO 吗？',
          'Is the ads budget on top of your fee?': '广告预算是在你们的费用之外吗？',
          'Keyword research': '关键词研究',
          'Keywords': '关键词',
          'How many search phrases we work on for your business.': '我们为您的业务负责多少个搜索词。',
          'A check of your website so we know what to fix first.': '先检查网站，确认该先修什么。',
          'We pick the phrases people type when they look for you.': '我们挑选人们找您时会输入的词组。',
          'The title and short text Google shows in search.': '谷歌在搜索结果里显示的标题与短文。',
          'Changes on your site, plus links from other sites.': '您网站上的改动，以及来自其他网站的链接。',
          'A report that shows how people find and use the site.': '显示人们如何找到并使用网站的报告。',
          'Written pages we publish to help you show up in search.': '为帮助您出现在搜索结果而发布的文章。',
          'Links from other websites that point people to yours.': '从其他网站指向您网站的链接。',
          'Your listing on Google Maps and local search.': '您在谷歌地图与本地搜索中的商家资料。',
          'A monthly note on where your phrases sit in Google.': '每月说明您的关键词在谷歌上的位置。',
          'A look at what similar businesses are doing in search.': '查看同类业务在搜索上的做法。',
          'How many phrases we aim to place on Google\'s first page.': '我们目标把多少个词组放上谷歌首页。',
          'The prepaid media budget for the ads, not our fee.': '广告的预付媒体预算，不是我们的服务费。',
          'How many ad sets we can run at the same time.': '我们可以同时运行多少组广告。',
          'A plan for what the ads say and who they should reach.': '广告说什么、该触达谁的计划。',
          'We keep adjusting the ads and try different versions.': '我们持续调整广告并测试不同版本。',
          'A report that shows what the ads send to your website.': '显示广告为网站带来什么的报告。',
          'A monthly note on results, with what to try next.': '每月结果说明，以及下一步建议。',
          'The ads are aimed at sales and enquiries, not just views.': '广告以销售与询盘为目标，而不只是浏览量。',
          'A one-page site built for the ads to send people to.': '为广告点击而建的单页网站。',
          'Meta title and description': '标题与描述',
          'Monthly ranking report': '每月排名报告',
          'On-page and off-page work': '站内与站外优化',
          'Once before we start': '开始前一次',
          'Your buyers are searching right now.': '您的客户此刻正在搜索。',
          'Performance in 12 months': '12 个月表现',
          'Plus plan': '加强配套',
          'Pro plan': '专业配套',
          'RM 10,000 and above': 'RM 10,000 及以上',
          'RM 2,000 to RM 4,999': 'RM 2,000 至 RM 4,999',
          'RM 5,000 to RM 9,999': 'RM 5,000 至 RM 9,999',
          'Sales and leads': '销售与潜在客户',
          'SEO plans': '**SEO** 配套',
          'Digital Marketing': '数字营销',
          'Rank for the phrases your buyers actually type into Google, and reach the rest with paid ads starting from the very first day following launch.': '让买家在 Google 搜索时就找到您，其余人群用广告从第一天起即刻触达。搜索持续累积资产，广告马上带来询盘，两者同时进行，更快更稳。',
          'You buy the position itself rather than rented clicks. Name the phrases your buyers type and we handle the research, the content and the links.': '您买的是排名本身，而不是一次次租来的点击。说出客户会输入的搜索词，研究、内容与链接都由我们做到上榜，询盘成本因此持续下降，越做越省。',
          'Site backlinks': '网站外链',
          'Standard plan': '标准配套',
          'Live this week, not next quarter.': '这个星期就上线，不用等下一季。',
          'Twice a year': '每年两次',
          'Enquiries arrive this week, rather than next quarter. We plan, test, and report on the campaigns for you, and adjust as the numbers roll in.': '询盘这个星期就来，不必等到下一季。广告活动由我们规划、测试与汇报，数据一有变化就马上调整，预算花在哪里也说得清清楚楚，绝对不藏私。',
          'Website audit': '网站审核',
          'What is the 5 percent on the Pro plan?': '专业配套的 5% 是什么？',
          'Every plan includes': '每个配套都包含',
          'Research before spend': '先研究，后投放',
          'We audit your site and your competitors first, then pick the phrases or audiences actually worth paying for.': '我们先审核您的网站和竞争对手，再挑出真正值得投放的搜索词或受众。',
          'Installed and configured on your site, so every enquiry can be traced back to the search or ad that produced it.': '在您的网站安装并配置完成，每一笔询盘都能追溯到来自哪个搜索或广告。',
          'One clear report each month: where you stand, what we spent, and what changes next.': '每月一份清晰的报告：您的排名位置、花费明细，以及下一步的调整。',
          'No setup fee': '无设置费',
          'You pay only for the work each month. There is nothing to pay up front to get started.': '每月只为实际服务付费，开始之前无需支付任何费用。',
          'Being found is the whole point.': '被找到，才是重点。',
          'SEO builds over months.\nAds start today.': 'SEO 需要数月累积。\n广告**今天就能开始**。',
          'Own page one for the phrases that pay': '让**会成交的词**稳坐首页',
          'Pause your advertising spending and the enquiries dry up on the very same day, whilst those pages and links you built with SEO keep working.': '暂停广告，询盘当天就停。用 SEO 建立的页面与外链会持续带来客户，而且没有人按点击向您收费；一边是租来的流量，一边是归您的资产。',
          'Buyers this week, on your budget': '这个星期就来客户，**预算由您定**',
          'Advertising places you in front of people who match your buyer profile. You control the budget, and the campaign can be paused at any moment.': '广告让您出现在已经符合客户画像的人面前。预算由您设定，随时可以暂停，花多少、停多久全由您决定，不会有绑约，也不会有任何隐藏的额外承诺。',
          'What does the SEO aim actually say?': 'SEO 的目标实际怎么说？',
          'Who manages the ads account?': '广告账户由谁管理？',
          'Who picks the keywords?': '关键词由谁挑选？',
          'Why use SEO': '为何使用 SEO',
          'Why use sponsored ads': '为何使用赞助广告',
          'We manage the ads account.': '广告账户由我们管理。',
          'faq-ads-now': '广告。SEO 排名以工作开始后 12 个月内来衡量。SEO 按月计费，不收设置费，也没有最短期限。广告账户、素材与落地页就绪后即可开始。',
          'faq-aim': '自工作开始起 12 个月内：标准配套目标至少 5 个关键词进入谷歌首页，加强配套 10 个，专业配套 15 个。无设置费。配套包含内容文章、外链与 Google 商家档案。',
          'faq-budget': '是。广告花费需预付。标准配套管理费每月 RM 680。加强配套为每月 RM 1,680。专业配套为每月 RM 2,680。预付媒体预算另计，区间如上。',
          'faq-channel': '谷歌、Facebook、TikTok，以及我们支持的其他平台，都在广告配套里讨论。收费相同。广告配套涵盖管理、测试、数据分析，以及以销售与询盘为目标的每月报告。',
          'faq-included': '不含。网页设计配套包含数据分析。SEO 标准、加强与专业配套是分开的按月产品，分别为每月 RM 1,980、RM 2,980 与 RM 3,980，对应 10、20 与 30 个我们负责的关键词。',
          'faq-keywords': '我们研究并建议人们实际会输入的词组。您核准名单。若每月大幅更换名单，那些词组的 12 个月排名周期会重新起算。',
          'faq-only-seo': '可以，前提是网站技术上够条件。开始时我们仍会审核一次。若网站无法排名，我们会在开始之前说明。SEO 按月计费，不收设置费。',
          'faq-own': '广告账户由我们为您管理。您把广告花费付给我们，由我们为账户充值，您不必自己开设或操作。',
          'faq-percent': '专业配套会对超过 RM 10,000 的广告花费加收 5%。这是在每月 RM 2,680 管理费之外。',
          'why-ads-account': '广告账户由我们为您管理。您把广告花费付给我们，由我们为账户充值，您不必自己开设或操作。',
          'why-ads-leads': '每个版本都会测试，预算只花在真正带来询盘的地方，而不是只买到点击次数。',
          'why-ads-now': '账户、素材与落地页就绪后广告就能跑，不用等 12 个月才有第一个询盘。',
          'why-ads-page': '加强配套与专业配套包含为广告而建的页面。标准配套把点击送到您已有的页面。',
          'why-seo-fit': '我们会在您付费之前先审核网站。若目前还无法排名，我们会在您决定付费之前就说明原因。',
          'why-seo-local': '围绕关键词撰写页面、Google 商家档案与地图列表，以及每月排名报告，全部包含。',
          'why-seo-programme': '每一个挣来的页面与外链都会留下，所以第二年从第一年结束的位置继续，而不是从零重来。',
          'why-seo-search': '每天都有人输入您卖的东西。没有 SEO，他们就会落在竞争对手那边。我们让您出现在那份名单里。'
        },
        'ms-MY': {
          'What you pay each month for the plan.': 'Apa yang anda bayar setiap bulan untuk pelan ini.',
          'Start with search': 'Mula dengan carian',
          'Grow your rankings': 'Kembangkan ranking anda',
          'Scale your reach': 'Luaskan capaian anda',
          'Start ranking for the phrases that matter most.': 'Mula ranking untuk frasa yang paling penting.',
          'More phrases, more pages, more ways to be found.': 'Lebih banyak frasa, lebih banyak halaman, lebih banyak cara ditemui.',
          'Maximum reach, with content and closer checks.': 'Capaian maksimum, dengan lebih kandungan dan semakan lebih rapat.',
          'Test your first campaign': 'Uji kempen pertama anda',
          'Grow your enquiries': 'Tambah pertanyaan anda',
          'Scale your spend': 'Luaskan perbelanjaan anda',
          'One campaign to prove what converts.': 'Satu kempen untuk membuktikan apa yang menukar jualan.',
          'More campaigns, plus a landing page for the click.': 'Lebih banyak kempen, plus halaman pendaratan untuk klik.',
          'Spend more, run more campaigns, keep the landing page.': 'Belanja lebih, jalankan lebih banyak kempen, kekalkan halaman pendaratan.',
          'No setup fee required for any of the plans.': 'Tiada yuran persediaan untuk mana-mana pelan.',
          'Rankings are measured on Google page 1 within 12 months of work starting.': 'Ranking diukur pada halaman 1 Google dalam 12 bulan kerja dimulakan.',
          'Ads spend is prepaid, and the range above is your media budget, not our fee.': 'Perbelanjaan iklan prabayar, dan julat di atas ialah bajet media anda, bukan yuran kami.',
          'Pro plan will add an additional 5% on the amount of ads spend above RM 10,000.': 'Pelan Pro menambah 5% lagi pada jumlah perbelanjaan iklan di atas RM 10,000.',
          '10 keywords on Google\'s first page': '10 kata kunci di halaman pertama Google',
          '15 keywords on Google\'s first page': '15 kata kunci di halaman pertama Google',
          '5 keywords on Google\'s first page': '5 kata kunci di halaman pertama Google',
          'It compounds. Year two starts higher.': 'Ia bertambah. Tahun kedua bermula lebih tinggi.',
          'You know before you pay.': 'Anda tahu sebelum anda bayar.',
          'Content and local search included.': 'Kandungan dan carian tempatan disertakan.',
          'A landing page built to close the click.': 'Halaman pendaratan yang dibina untuk menutup klik.',
          'Ads management plans': 'Pelan **Pengurusan Iklan**',
          'Ads spend': 'Perbelanjaan iklan',
          'Built to bring enquiries, not views.': 'Dibina untuk membawa pertanyaan, bukan tontonan.',
          'Campaign tests': 'Ujian kempen',
          'Campaigns': 'Kempen',
          'Competitor analysis': 'Analisis pesaing',
          'Content posts': 'Siaran kandungan',
          'Content strategy': 'Strategi kandungan',
          'Your buyers are already searching Google. SEO claims the phrases that matter, so page one keeps paying you back in steady, free organic enquiries.': 'Pelanggan anda sudah mencari di Google apa yang anda jual. SEO merebut frasa penting itu, jadi halaman satu terus membawa pertanyaan percuma.',
          'Every 3 months': 'Setiap 3 bulan',
          'Free landing page': 'Halaman pendaratan percuma',
          'What SEO and paid advertising actually cover, how our fee sits separately from the ad budget, and exactly what you should expect before you commit.': 'Apa yang sebenarnya diliputi SEO dan iklan, bagaimana yuran kami berbeza daripada bajet iklan, dan apa yang perlu anda jangkakan sebelum mula.',
          'Google Business Profile': 'Profil Perniagaan Google',
          'Google, Facebook or TikTok?': 'Google, Facebook atau TikTok?',
          'I already have a site. Can you only do SEO?': 'Saya sudah ada laman web. Boleh SEO sahaja?',
          'I need traffic now. Is that SEO or ads?': 'Saya perlukan trafik sekarang. SEO atau iklan?',
          'Is SEO included in web design fees?': 'Adakah SEO termasuk dalam yuran reka bentuk web?',
          'Is the ads budget on top of your fee?': 'Adakah bajet iklan di atas yuran anda?',
          'Keyword research': 'Penyelidikan kata kunci',
          'Keywords': 'Kata kunci',
          'How many search phrases we work on for your business.': 'Berapa banyak frasa carian yang kami kerjakan untuk perniagaan anda.',
          'A check of your website so we know what to fix first.': 'Semakan laman web anda supaya kami tahu apa yang perlu dibaiki dahulu.',
          'We pick the phrases people type when they look for you.': 'Kami pilih frasa yang orang taip apabila mereka cari anda.',
          'The title and short text Google shows in search.': 'Tajuk dan teks pendek yang Google tunjuk dalam carian.',
          'Changes on your site, plus links from other sites.': 'Perubahan pada laman anda, plus pautan dari laman lain.',
          'A report that shows how people find and use the site.': 'Laporan yang menunjukkan bagaimana orang jumpa dan guna laman.',
          'Written pages we publish to help you show up in search.': 'Halaman bertulis yang kami terbit untuk bantu anda muncul dalam carian.',
          'Links from other websites that point people to yours.': 'Pautan dari laman web lain yang hantar orang ke laman anda.',
          'Your listing on Google Maps and local search.': 'Penyenaraian anda pada Google Maps dan carian tempatan.',
          'A monthly note on where your phrases sit in Google.': 'Nota bulanan tentang kedudukan frasa anda di Google.',
          'A look at what similar businesses are doing in search.': 'Tinjauan apa yang perniagaan serupa buat dalam carian.',
          'How many phrases we aim to place on Google\'s first page.': 'Berapa banyak frasa yang kami sasar letak di halaman pertama Google.',
          'The prepaid media budget for the ads, not our fee.': 'Bajet media prabayar untuk iklan, bukan yuran kami.',
          'How many ad sets we can run at the same time.': 'Berapa banyak set iklan yang kami boleh jalankan pada masa yang sama.',
          'A plan for what the ads say and who they should reach.': 'Rancangan tentang apa yang iklan kata dan siapa yang patut dicapai.',
          'We keep adjusting the ads and try different versions.': 'Kami terus laras iklan dan cuba versi berbeza.',
          'A report that shows what the ads send to your website.': 'Laporan yang menunjukkan apa yang iklan hantar ke laman web anda.',
          'A monthly note on results, with what to try next.': 'Nota bulanan tentang hasil, dengan apa yang patut dicuba seterusnya.',
          'The ads are aimed at sales and enquiries, not just views.': 'Iklan ditujukan kepada jualan dan pertanyaan, bukan sekadar tontonan.',
          'A one-page site built for the ads to send people to.': 'Laman satu halaman yang dibina untuk iklan menghantar orang ke situ.',
          'Meta title and description': 'Tajuk dan perihalan meta',
          'Monthly ranking report': 'Laporan ranking bulanan',
          'On-page and off-page work': 'Kerja pada laman dan luar laman',
          'Once before we start': 'Sekali sebelum kami mula',
          'Your buyers are searching right now.': 'Pembeli anda sedang mencari sekarang.',
          'Performance in 12 months': 'Prestasi dalam 12 bulan',
          'Plus plan': 'Pelan Plus',
          'Pro plan': 'Pelan Pro',
          'RM 10,000 and above': 'RM 10,000 dan ke atas',
          'RM 2,000 to RM 4,999': 'RM 2,000 hingga RM 4,999',
          'RM 5,000 to RM 9,999': 'RM 5,000 hingga RM 9,999',
          'Sales and leads': 'Jualan dan petunjuk',
          'SEO plans': 'Pelan **SEO**',
          'Digital Marketing': 'Pemasaran Digital',
          'Rank for the phrases your buyers actually type into Google, and reach the rest with paid ads starting from the very first day following launch.': 'Tampil apabila pembeli anda mencari di Google, dan jangkau selebihnya dengan iklan berbayar bermula daripada hari pertama lagi selepas dilancarkan.',
          'You buy the position itself rather than rented clicks. Name the phrases your buyers type and we handle the research, the content and the links.': 'Anda membeli kedudukan itu sendiri, bukan menyewa klik. Namakan frasa pembeli anda taip dan kami uruskan penyelidikan, kandungan serta pautan.',
          'Site backlinks': 'Pautan balik tapak',
          'Standard plan': 'Pelan Standard',
          'Live this week, not next quarter.': 'Hidup minggu ini, bukan suku depan.',
          'Twice a year': 'Dua kali setahun',
          'Enquiries arrive this week, rather than next quarter. We plan, test, and report on the campaigns for you, and adjust as the numbers roll in.': 'Pertanyaan masuk minggu ini, bukan suku tahun depan. Kami merancang, menguji dan melaporkan kempen untuk anda, serta menyesuaikannya bila data masuk.',
          'Website audit': 'Audit laman web',
          'What is the 5 percent on the Pro plan?': 'Apa 5 peratus pada pelan Pro?',
          'Every plan includes': 'Setiap pelan merangkumi',
          'Research before spend': 'Kaji dahulu sebelum berbelanja',
          'We audit your site and your competitors first, then pick the phrases or audiences actually worth paying for.': 'Kami audit laman anda dan pesaing dahulu, kemudian pilih frasa atau audiens yang benar-benar berbaloi dibayar.',
          'Installed and configured on your site, so every enquiry can be traced back to the search or ad that produced it.': 'Dipasang dan dikonfigurasikan pada laman anda, jadi setiap pertanyaan boleh dikesan kembali kepada carian atau iklan yang menghasilkannya.',
          'One clear report each month: where you stand, what we spent, and what changes next.': 'Satu laporan jelas setiap bulan: kedudukan anda, perbelanjaan kami, dan apa yang berubah seterusnya.',
          'No setup fee': 'Tiada yuran persediaan',
          'You pay only for the work each month. There is nothing to pay up front to get started.': 'Anda bayar hanya untuk kerja setiap bulan. Tiada bayaran pendahuluan untuk bermula.',
          'Being found is the whole point.': 'Ditemui adalah matlamat utamanya.',
          'SEO builds over months.\nAds start today.': 'SEO dibina dalam beberapa bulan.\nIklan bermula **hari ini**.',
          'Own page one for the phrases that pay': 'Miliki halaman satu untuk **frasa yang membayar**',
          'Pause your advertising spending and the enquiries dry up on the very same day, whilst those pages and links you built with SEO keep working.': 'Hentikan iklan dan pertanyaan berhenti hari itu juga. Halaman dan pautan yang dibina dengan SEO terus berfungsi, tiada siapa mengebilkan klik anda.',
          'Buyers this week, on your budget': 'Pembeli minggu ini, pada **bajet anda**',
          'Advertising places you in front of people who match your buyer profile. You control the budget, and the campaign can be paused at any moment.': 'Iklan meletakkan anda di hadapan orang yang sudah sepadan dengan pembeli anda. Anda tetapkan bajet dan boleh menjeda kempen pada bila-bila masa.',
          'What does the SEO aim actually say?': 'Apa yang matlamat SEO sebenarnya katakan?',
          'Who manages the ads account?': 'Siapa yang menguruskan akaun iklan?',
          'Who picks the keywords?': 'Siapa memilih kata kunci?',
          'Why use SEO': 'Kenapa guna SEO',
          'Why use sponsored ads': 'Kenapa guna iklan tajaan',
          'We manage the ads account.': 'Kami uruskan akaun iklan.',
          'faq-ads-now': 'Iklan. Ranking SEO diukur dalam 12 bulan kerja dimulakan. SEO dibilkan setiap bulan, tanpa yuran persediaan dan tanpa tempoh minimum. Iklan boleh bermula sebaik akaun, kreatif dan halaman pendaratan sedia.',
          'faq-aim': 'Dalam 12 bulan kerja dimulakan: pelan Standard mensasarkan sekurang-kurangnya 5 kata kunci di halaman satu Google, pelan Plus 10, pelan Pro 15. Tiada yuran persediaan. Siaran kandungan, pautan balik dan Profil Perniagaan Google ada dalam pelan.',
          'faq-budget': 'Ya. Perbelanjaan iklan prabayar. Pengurusan pelan Standard ialah RM 680 sebulan. Pelan Plus ialah RM 1,680. Pelan Pro ialah RM 2,680. Media prabayar di atas itu dalam julat di atas.',
          'faq-channel': 'Google, Facebook, TikTok dan platform lain yang kami sokong semuanya dibincangkan bersama pelan iklan. Harganya sama. Pelan iklan merangkumi pengurusan, ujian, analitik dan laporan bulanan yang mensasarkan jualan dan pertanyaan.',
          'faq-included': 'Tidak. Pelan reka bentuk web merangkumi analitik. Pelan SEO Standard, Plus dan Pro ialah produk bulanan berasingan pada RM 1,980, RM 2,980 dan RM 3,980, dengan 10, 20 dan 30 kata kunci yang kami kerjakan.',
          'faq-keywords': 'Kami menyelidik dan mencadangkan frasa yang orang benar-benar taip. Anda meluluskan senarai. Menukar seluruh senarai setiap bulan menetapkan semula tempoh ranking 12 bulan untuk frasa itu.',
          'faq-only-seo': 'Ya, jika laman web sesuai dari segi teknikal. Kami masih audit sekali pada permulaan. Jika laman tidak dapat ranking, kami akan katakan sebelum anda bermula. SEO dibilkan setiap bulan, tanpa yuran persediaan.',
          'faq-own': 'Kami uruskan akaun iklan untuk anda. Anda bayar perbelanjaan iklan kepada kami dan kami tambah nilai akaun, jadi tiada apa yang perlu anda sediakan.',
          'faq-percent': 'Pelan Pro menambah 5 peratus pada jumlah perbelanjaan iklan di atas RM 10,000. Itu di atas yuran pengurusan RM 2,680.',
          'why-ads-account': 'Kami uruskan akaun iklan untuk anda. Anda bayar perbelanjaan iklan kepada kami dan kami tambah nilai akaun, jadi tiada apa yang perlu anda sediakan.',
          'why-ads-leads': 'Setiap versi diuji, jadi bajet pergi kepada apa yang benar-benar membawa pertanyaan, bukan sekadar mengumpul klik.',
          'why-ads-now': 'Sebaik akaun, kreatif dan halaman pendaratan sedia, iklan berjalan. Tiada tunggu 12 bulan untuk pertanyaan pertama.',
          'why-ads-page': 'Plus dan Pro termasuk halaman yang direka khas untuk iklan. Standard menghantar klik ke halaman yang anda sudah ada.',
          'why-seo-fit': 'Kami audit laman dahulu. Jika ia belum boleh ranking, kami beritahu sebelum anda membayar, bukan selepas tiga bulan.',
          'why-seo-local': 'Halaman yang ditulis sekitar kata kunci anda, Profil Perniagaan Google dan senarai peta, serta laporan ranking bulanan.',
          'why-seo-programme': 'Setiap halaman dan pautan yang anda peroleh kekal, jadi tahun kedua bermula di tempat tahun pertama tamat, bukan dari kosong.',
          'why-seo-search': 'Setiap hari ada orang menaip apa yang anda jual. Tanpa SEO mereka akan mendarat pada pesaing, bukan pada anda sendiri.'
        }
      }
    });

    const yes = { type: 'yes' };
    const no = { type: 'no' };

    const seoColumns = computed(() => [
      {
        name: t('Standard plan'),
        audience: t('Start with search'),
        icon: 'magnifying-glass',
        blurb: t('Start ranking for the phrases that matter most.'),
        price: 'RM 1,980',
        unit: t('per month, billed monthly')
      },
      {
        name: t('Plus plan'),
        audience: t('Grow your rankings'),
        icon: 'chart-line',
        badge: t('Most Popular'),
        blurb: t('More phrases, more pages, more ways to be found.'),
        price: 'RM 2,980',
        unit: t('per month, billed monthly'),
        hit: true
      },
      {
        name: t('Pro plan'),
        audience: t('Scale your reach'),
        icon: 'globe',
        blurb: t('Maximum reach, with content and closer checks.'),
        price: 'RM 3,980',
        unit: t('per month, billed monthly')
      }
    ]);

    const seoRows = computed(() => [
      { label: t('Keywords'), hint: t('How many search phrases we work on for your business.'), cells: [{ type: 'text', value: '10' }, { type: 'text', value: '20' }, { type: 'text', value: '30' }] },
      { label: t('Website audit'), hint: t('A check of your website so we know what to fix first.'), cells: [yes, yes, yes] },
      { label: t('Keyword research'), hint: t('We pick the phrases people type when they look for you.'), cells: [yes, yes, yes] },
      { label: t('Meta title and description'), hint: t('The title and short text Google shows in search.'), cells: [yes, yes, yes] },
      { label: t('On-page and off-page work'), hint: t('Changes on your site, plus links from other sites.'), cells: [yes, yes, yes] },
      { label: t('Google Analytics 4'), hint: t('A report that shows how people find and use the site.'), cells: [yes, yes, yes] },
      { label: t('Content posts'), hint: t('Written pages we publish to help you show up in search.'), cells: [{ type: 'text', value: '2' }, { type: 'text', value: '3' }, { type: 'text', value: '4' }] },
      { label: t('Site backlinks'), hint: t('Links from other websites that point people to yours.'), cells: [yes, yes, yes] },
      { label: t('Google Business Profile'), hint: t('Your listing on Google Maps and local search.'), cells: [yes, yes, yes] },
      { label: t('Monthly ranking report'), hint: t('A monthly note on where your phrases sit in Google.'), cells: [yes, yes, yes] },
      { label: t('Competitor analysis'), hint: t('A look at what similar businesses are doing in search.'), cells: [{ type: 'text', value: t('Once before we start') }, { type: 'text', value: t('Twice a year') }, { type: 'text', value: t('Every 3 months') }] },
      { label: t('Performance in 12 months'), hint: t('How many phrases we aim to place on Google\'s first page.'), cells: [{ type: 'text', value: t('5 keywords on Google\'s first page') }, { type: 'text', value: t('10 keywords on Google\'s first page') }, { type: 'text', value: t('15 keywords on Google\'s first page') }] }
    ]);

    const adsColumns = computed(() => [
      {
        name: t('Standard plan'),
        audience: t('Test your first campaign'),
        icon: 'bullhorn',
        blurb: t('One campaign to prove what converts.'),
        price: 'RM 680',
        unit: t('per month, billed monthly')
      },
      {
        name: t('Plus plan'),
        audience: t('Grow your enquiries'),
        icon: 'bullseye',
        badge: t('Most Popular'),
        blurb: t('More campaigns, plus a landing page for the click.'),
        price: 'RM 1,680',
        unit: t('per month, billed monthly'),
        hit: true
      },
      {
        name: t('Pro plan'),
        audience: t('Scale your spend'),
        icon: 'chart-line',
        blurb: t('Spend more, run more campaigns, keep the landing page.'),
        price: 'RM 2,680',
        unit: t('per month, billed monthly')
      }
    ]);

    const adsRows = computed(() => [
      { label: t('Ads spend'), hint: t('The prepaid media budget for the ads, not our fee.'), cells: [{ type: 'text', value: t('RM 2,000 to RM 4,999') }, { type: 'text', value: t('RM 5,000 to RM 9,999') }, { type: 'text', value: t('RM 10,000 and above') }] },
      { label: t('Campaigns'), hint: t('How many ad sets we can run at the same time.'), cells: [{ type: 'text', value: '1' }, { type: 'text', value: '5' }, { type: 'text', value: '10' }] },
      { label: t('Content strategy'), hint: t('A plan for what the ads say and who they should reach.'), cells: [yes, yes, yes] },
      { label: t('Campaign tests'), hint: t('We keep adjusting the ads and try different versions.'), cells: [yes, yes, yes] },
      { label: t('Google Analytics 4'), hint: t('A report that shows what the ads send to your website.'), cells: [yes, yes, yes] },
      { label: t('Monthly report'), hint: t('A monthly note on results, with what to try next.'), cells: [yes, yes, yes] },
      { label: t('Sales and leads'), hint: t('The ads are aimed at sales and enquiries, not just views.'), cells: [yes, yes, yes] },
      { label: t('Free landing page'), hint: t('A one-page site built for the ads to send people to.'), cells: [no, yes, yes] }
    ]);

    const dmIncludes = computed(() => [
      {
        icon: 'magnifying-glass',
        title: t('Research before spend'),
        body: t('We audit your site and your competitors first, then pick the phrases or audiences actually worth paying for.')
      },
      {
        icon: 'chart-line',
        title: t('Google Analytics 4'),
        body: t('Installed and configured on your site, so every enquiry can be traced back to the search or ad that produced it.')
      },
      {
        icon: 'clipboard-check',
        title: t('Monthly report'),
        body: t('One clear report each month: where you stand, what we spent, and what changes next.')
      },
      {
        icon: 'circle-check',
        title: t('No setup fee'),
        body: t('You pay only for the work each month. There is nothing to pay up front to get started.')
      }
    ]);

    const seoWhy = computed(() => [
      { icon: 'magnifying-glass', title: t('Your buyers are searching right now.'), body: t('why-seo-search') },
      { icon: 'calendar-check', title: t('It compounds. Year two starts higher.'), body: t('why-seo-programme') },
      { icon: 'clipboard-check', title: t('You know before you pay.'), body: t('why-seo-fit') },
      { icon: 'location-dot', title: t('Content and local search included.'), body: t('why-seo-local') }
    ]);

    const adsWhy = computed(() => [
      { icon: 'bolt', title: t('Live this week, not next quarter.'), body: t('why-ads-now') },
      { icon: 'bullseye', title: t('Built to bring enquiries, not views.'), body: t('why-ads-leads') },
      { icon: 'wallet', title: t('We manage the ads account.'), body: t('why-ads-account') },
      { icon: 'arrow-pointer', title: t('A landing page built to close the click.'), body: t('why-ads-page') }
    ]);

    const faqs = computed(() => [
      { q: t('Is SEO included in web design fees?'), a: t('faq-included') },
      { q: t('What does the SEO aim actually say?'), a: t('faq-aim') },
      { q: t('I already have a site. Can you only do SEO?'), a: t('faq-only-seo') },
      { q: t('Who picks the keywords?'), a: t('faq-keywords') },
      { q: t('I need traffic now. Is that SEO or ads?'), a: t('faq-ads-now') },
      { q: t('Is the ads budget on top of your fee?'), a: t('faq-budget') },
      { q: t('What is the 5 percent on the Pro plan?'), a: t('faq-percent') },
      { q: t('Google, Facebook or TikTok?'), a: t('faq-channel') },
      { q: t('Who manages the ads account?'), a: t('faq-own') }
    ]);

    const seoRemarks = computed(() => [
      t('No setup fee required for any of the plans.'),
      t('Rankings are measured on Google page 1 within 12 months of work starting.')
    ]);

    const adsRemarks = computed(() => [
      t('No setup fee required for any of the plans.'),
      t('Ads spend is prepaid, and the range above is your media budget, not our fee.'),
      t('Pro plan will add an additional 5% on the amount of ads spend above RM 10,000.')
    ]);

    return { t, dmIncludes, seoColumns, seoRows, adsColumns, adsRows, seoRemarks, adsRemarks, seoWhy, adsWhy, faqs };
  }
};
</script>
